import { useState } from "react";
import DOMPurify from "dompurify";
import { useParams, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useOrder, useUpdateOrder, useOrderNotes, useAddOrderNote } from "@/hooks/use-orders";
import { useActiveStore } from "@/hooks/use-active-store";
import { useSession } from "@/hooks/use-auth";
import { hasRole } from "@/lib/roles";
import { StatusBadge, ORDER_STATUSES } from "@/components/orders/status-badge";
import { OrderItems } from "@/components/orders/order-items";
import { AddressCard } from "@/components/orders/address-card";
import { AddItemDialog } from "@/components/orders/add-item-dialog";
import { CustomerCard } from "@/components/orders/customer-card";
import { OrderNotes, type NoteKind } from "@/components/orders/order-notes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, MapPin, Truck, StickyNote, Loader2, ShieldBan, Repeat,
  Link as LinkIcon, MoreHorizontal, CreditCard, PackageX, Check,
} from "lucide-react";
import { ExchangeBadge } from "@/components/orders/exchange-badge";
import { toast } from "sonner";
import { formatDateTime, formatDate, formatCurrency, plural } from "@/lib/utils";
import { useCheckOrderBanned, useUnbanCustomer } from "@/hooks/use-banned-customers";
import { useCustomerContext } from "@/hooks/use-customer-context";
import { BanCustomerDialog } from "@/components/banned-customers/ban-dialog";

/** Adrese su iste kad se sva vidljiva polja poklapaju — tad se dostava sažima
 *  u jedan red umjesto da ponavlja isti blok. */
function sameAddress(a: any, b: any): boolean {
  if (!a || !b) return false;
  const keys = ["first_name", "last_name", "company", "address_1", "address_2", "city", "state", "postcode", "country"];
  return keys.every((k) => String(a[k] ?? "").trim() === String(b[k] ?? "").trim());
}

export function OrderDetailPage() {
  const { orderId } = useParams({ strict: false });
  const { storeId } = useActiveStore();
  const { user } = useSession();
  const canEdit = hasRole(user, "admin") || hasRole(user, "manager");

  const { data: order, isLoading } = useOrder(storeId, orderId ?? null);
  const { data: notes } = useOrderNotes(storeId, orderId ?? null);
  const updateOrder = useUpdateOrder(storeId);
  const addNote = useAddOrderNote(storeId);
  const customerContext = useCustomerContext(storeId, orderId ?? null);

  const shareLinkMutation = useMutation({
    mutationFn: async (): Promise<{ url: string; expiresAt: number }> => {
      const res = await fetch(`/api/orders/${orderId}/share-link?storeId=${storeId}`, {
        method: "POST",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || "Greška pri generisanju linka");
      }
      return res.json();
    },
    onSuccess: async (data) => {
      try {
        await navigator.clipboard.writeText(data.url);
        toast.success(`Link kopiran, važi do ${formatDateTime(new Date(data.expiresAt))}`);
      } catch {
        toast.success(`Link generisan: ${data.url}`);
      }
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const [addItemOpen, setAddItemOpen] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [showShipping, setShowShipping] = useState(false);
  const bannedCheck = useCheckOrderBanned(order?.billing?.email, order?.billing?.phone);
  const unbanMutation = useUnbanCustomer();
  const isBanned = !!bannedCheck.data;

  async function handleToggleExchange(next: boolean) {
    try {
      await updateOrder.mutateAsync({
        orderId: Number(orderId),
        meta_data: [{ key: "_komoran_is_exchange", value: next ? "1" : "" }],
      });
      toast.success(next ? "Označeno kao zamjenska pošiljka" : "Oznaka zamjenske pošiljke uklonjena");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri ažuriranju oznake");
    }
  }

  async function handleStatusChange(status: string) {
    try {
      await updateOrder.mutateAsync({ orderId: Number(orderId), status });
      toast.success("Status ažuriran");
    } catch (e: any) { toast.error(e?.message || "Greška pri promjeni statusa"); }
  }

  async function handleUpdateItem(itemId: number, quantity: number) {
    const lineItems = order.line_items.map((li: any) =>
      li.id === itemId ? { id: li.id, quantity } : { id: li.id }
    );
    try {
      await updateOrder.mutateAsync({ orderId: Number(orderId), line_items: lineItems });
      toast.success("Stavka ažurirana");
    } catch (e: any) { toast.error(e?.message || "Greška pri ažuriranju stavke"); }
  }

  async function handleRemoveItem(itemId: number) {
    const lineItems = order.line_items
      .filter((li: any) => li.id !== itemId)
      .map((li: any) => ({ id: li.id }));
    lineItems.push({ id: itemId, quantity: 0 });
    try {
      await updateOrder.mutateAsync({ orderId: Number(orderId), line_items: lineItems });
      toast.success("Stavka uklonjena");
    } catch (e: any) { toast.error(e?.message || "Greška pri uklanjanju stavke"); }
  }

  async function handleAddItem(productId: number, variationId: number | undefined, quantity: number) {
    const newItem: any = { product_id: productId, quantity };
    if (variationId !== undefined) newItem.variation_id = variationId;
    const lineItems = [
      ...order.line_items.map((li: any) => ({ id: li.id })),
      newItem,
    ];
    try {
      await updateOrder.mutateAsync({ orderId: Number(orderId), line_items: lineItems });
      toast.success("Stavka dodana");
    } catch (e: any) { toast.error(e?.message || "Greška pri dodavanju stavke"); }
  }

  async function handleSaveBilling(billing: any) {
    try {
      await updateOrder.mutateAsync({ orderId: Number(orderId), billing });
      toast.success("Adresa za naplatu ažurirana");
    } catch (e: any) { toast.error(e?.message || "Greška pri ažuriranju adrese"); }
  }

  async function handleSaveShipping(shipping: any) {
    try {
      await updateOrder.mutateAsync({ orderId: Number(orderId), shipping });
      toast.success("Adresa za dostavu ažurirana");
    } catch (e: any) { toast.error(e?.message || "Greška pri ažuriranju adrese"); }
  }

  async function handleAddNote() {
    if (!noteText.trim()) return;
    try {
      await addNote.mutateAsync({ orderId: Number(orderId), note: noteText });
      setNoteText("");
      toast.success("Bilješka dodana");
    } catch (e: any) { toast.error(e?.message || "Greška pri dodavanju bilješke"); }
  }

  async function handleSaveNote(kind: NoteKind, value: string) {
    const key = kind === "admin" ? "_greenbox_important_note" : "_komoran_internal_note";
    try {
      await updateOrder.mutateAsync({
        orderId: Number(orderId),
        meta_data: [{ key, value: value.trim() }],
      });
      toast.success(kind === "admin" ? "Admin napomena spremljena" : "Interna napomena spremljena");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri spremanju napomene");
      throw e;
    }
  }

  const savedInternalNote = order?.meta_data?.find((m: any) => m.key === "_komoran_internal_note")?.value || "";
  const savedAdminNote = order?.meta_data?.find((m: any) => m.key === "_greenbox_important_note")?.value || "";
  const savedCustomerNote = String(order?.customer_note ?? "");
  const savedIsExchange = !!order?.meta_data?.find((m: any) => m.key === "_komoran_is_exchange")?.value;

  if (isLoading) {
    return (
      <div className="p-4 flex flex-col gap-3">
        <Skeleton className="h-8 w-full" />
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-56 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
          <div className="flex flex-col gap-3">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!order) return <p className="p-4 text-xs text-muted-foreground">Narudžba nije pronađena</p>;

  const ctx = customerContext.data;
  const shippingIsSame = sameAddress(order.billing, order.shipping);
  // Woo narudžba nema `shipping_method_title` — naziv dostave živi u `shipping_lines`.
  const shippingMethod = (Array.isArray(order.shipping_lines) ? order.shipping_lines : [])
    .map((l: any) => l?.method_title)
    .filter((t: any) => typeof t === "string" && t.length > 0)
    .join(", ");

  return (
    <div className="p-4 flex flex-col gap-3">
      {updateOrder.isPending && (
        <div className="fixed top-0 left-0 right-0 z-50 h-0.5 bg-muted overflow-hidden">
          <div className="h-full w-1/3 bg-foreground animate-[slide_1s_ease-in-out_infinite]" />
        </div>
      )}

      {/* Zaglavlje ostaje pri vrhu — status i akcije su potrebni i na dnu duge narudžbe. */}
      <div className="sticky top-0 z-20 -mx-4 -mt-4 px-4 py-2.5 bg-background border-b flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <Link to="/orders">
            <Button variant="ghost" size="icon-xs">
              <ArrowLeft className="h-3.5 w-3.5" />
            </Button>
          </Link>
          <h2 className="text-sm font-medium tabular-nums">#{order.number}</h2>
          <StatusBadge status={order.status} />
          <span className="text-xs text-muted-foreground hidden sm:inline tabular-nums">
            {formatDate(order.date_created)}
          </span>
          {savedIsExchange && <ExchangeBadge />}
          {ctx && (
            <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground tabular-nums">
              {ctx.orderIndex}. narudžba
            </span>
          )}
          {ctx && ctx.packageReturns.count > 0 && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] text-red-600">
              <PackageX className="h-3 w-3" />
              {ctx.packageReturns.count} {plural(ctx.packageReturns.count, "odbijena", "odbijene", "odbijenih")}
            </span>
          )}
          {isBanned && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.08em] text-red-600">
              <ShieldBan className="h-3 w-3" />
              Banovan
            </span>
          )}
        </div>

        {canEdit && (
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => shareLinkMutation.mutate()}
              disabled={shareLinkMutation.isPending}
              className="gap-1.5"
            >
              <LinkIcon className="h-3.5 w-3.5" />
              {shareLinkMutation.isPending ? "Generisanje..." : "Share link"}
            </Button>
            <Select value={order.status} onValueChange={handleStatusChange} disabled={updateOrder.isPending}>
              <SelectTrigger size="sm" className="w-full sm:w-36">
                {updateOrder.isPending ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Ažuriranje...
                  </span>
                ) : (
                  <SelectValue placeholder="Status" />
                )}
              </SelectTrigger>
              <SelectContent>
                {ORDER_STATUSES.filter((s) => s.value).map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon-sm" aria-label="Više akcija">
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                  Narudžba
                </DropdownMenuLabel>
                <DropdownMenuItem
                  onSelect={() => handleToggleExchange(!savedIsExchange)}
                  disabled={updateOrder.isPending}
                >
                  <Repeat className="h-3.5 w-3.5" />
                  Zamjenska pošiljka
                  {savedIsExchange && <Check className="h-3.5 w-3.5 ml-auto" />}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                  Kupac
                </DropdownMenuLabel>
                {isBanned ? (
                  <DropdownMenuItem
                    disabled={unbanMutation.isPending}
                    onSelect={async () => {
                      try {
                        await unbanMutation.mutateAsync(bannedCheck.data!.id);
                        toast.success("Ban uklonjen");
                      } catch (e: any) {
                        toast.error(e?.message || "Greška pri uklanjanju bana");
                      }
                    }}
                  >
                    <ShieldBan className="h-3.5 w-3.5" />
                    Ukloni ban
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem variant="destructive" onSelect={() => setBanDialogOpen(true)}>
                    <ShieldBan className="h-3.5 w-3.5" />
                    Banuj kupca
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3">
        {/* Lijevo: stavke, napomene, aktivnost */}
        <div className="flex flex-col gap-3 min-w-0">
          <OrderItems
            items={order.line_items}
            currency={order.currency}
            subtotal={order.subtotal}
            shippingTotal={order.shipping_total}
            total={order.total}
            canEdit={canEdit}
            onUpdateItem={handleUpdateItem}
            onRemoveItem={handleRemoveItem}
            onAddItem={() => setAddItemOpen(true)}
          />

          <OrderNotes
            customerNote={savedCustomerNote}
            adminNote={savedAdminNote}
            internalNote={savedInternalNote}
            canEdit={canEdit}
            saving={updateOrder.isPending}
            onSave={handleSaveNote}
          />

          <div className="ring-1 ring-foreground/10">
            <div className="flex items-center gap-1.5 px-3 py-2.5 border-b">
              <StickyNote className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs font-medium">Aktivnost</span>
            </div>
            <div className="p-3 flex flex-col gap-2">
              {notes?.length === 0 && (
                <p className="text-xs text-muted-foreground py-2 text-center">Još nema zapisa.</p>
              )}
              {notes?.map((note: any) => (
                <div key={note.id} className="border-b pb-2 last:border-0 last:pb-0">
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {formatDateTime(note.date_created)}
                  </span>
                  <div className="text-xs mt-0.5 [&_p]:leading-relaxed" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.note) }} />
                </div>
              ))}
              {canEdit && (
                <div className="flex gap-2 mt-1 pt-2 border-t">
                  <Input
                    placeholder="Dodaj bilješku..."
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddNote()}
                    className="flex-1"
                  />
                  <Button size="sm" onClick={handleAddNote} disabled={addNote.isPending} className="gap-1.5">
                    {addNote.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                    {addNote.isPending ? "Dodavanje..." : "Dodaj"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Desno: kupac, plaćanje, adrese */}
        <div className="flex flex-col gap-3">
          <CustomerCard
            name={`${order.billing?.first_name || ""} ${order.billing?.last_name || ""}`.trim()}
            email={order.billing?.email}
            phone={order.billing?.phone}
            isBanned={isBanned}
            banReason={bannedCheck.data?.reason}
            context={ctx}
            isLoading={customerContext.isLoading}
          />

          <div className="ring-1 ring-foreground/10">
            <div className="flex items-center gap-1.5 px-3 py-2.5 border-b">
              <CreditCard className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs font-medium">Plaćanje i dostava</span>
            </div>
            <div className="divide-y divide-foreground/10">
              <div className="flex items-baseline justify-between gap-2 px-3 py-2">
                <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Plaćanje</span>
                <span className="text-xs text-right">{order.payment_method_title || "—"}</span>
              </div>
              <div className="flex items-baseline justify-between gap-2 px-3 py-2">
                <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Dostava</span>
                <span className="text-xs text-right">{shippingMethod || "—"}</span>
              </div>
              <div className="flex items-baseline justify-between gap-2 px-3 py-2">
                <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Ukupno</span>
                <span className="text-xs font-medium tabular-nums">
                  {formatCurrency(Number(order.total) || 0, order.currency)}
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
            <AddressCard
              title="Adresa za naplatu"
              icon={<MapPin className="h-3 w-3 text-muted-foreground" />}
              address={order.billing}
              canEdit={canEdit}
              onSave={handleSaveBilling}
            />
            {shippingIsSame && !showShipping ? (
              <div className="ring-1 ring-foreground/10">
                <button
                  className="flex w-full items-center gap-1.5 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors"
                  onClick={() => setShowShipping(true)}
                >
                  <Truck className="h-3 w-3 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Dostava: isto kao naplata</span>
                </button>
              </div>
            ) : (
              <AddressCard
                title="Adresa za dostavu"
                icon={<Truck className="h-3 w-3 text-muted-foreground" />}
                address={order.shipping}
                canEdit={canEdit}
                onSave={handleSaveShipping}
              />
            )}
          </div>
        </div>
      </div>

      <BanCustomerDialog
        open={banDialogOpen}
        onClose={() => setBanDialogOpen(false)}
        customer={{
          email: order.billing?.email || "",
          phone: order.billing?.phone || "",
          name: `${order.billing?.first_name || ""} ${order.billing?.last_name || ""}`.trim(),
        }}
        storeId={storeId ?? undefined}
      />
      <AddItemDialog open={addItemOpen} onClose={() => setAddItemOpen(false)} onAdd={handleAddItem} />
    </div>
  );
}
